/**
 * `#uc2Compare` — the reference watershed and one of its three similar ones, side by side
 * (client §3.2: "the existing side-by-side comparison with the same three charts for
 * both").
 *
 * The launch round replaces the old dimension/profile/GAEZ table with the card's own three
 * charts: `GET {uc}/compare?a=&b=` returns the two `{uc}/units/{id}` payloads, which since
 * the launch definition carry `series`, `rain_mean`, `adoption` and `programme_means` —
 * everything `Uc2Charts` draws. The GAEZ block and its ids are gone with the metrics the
 * definition dropped, and so is the soil-profile table: the profile is *how* the two were
 * matched, not something a reader is asked to compare.
 */
import { useTranslation } from 'react-i18next';

import { useIntelCompare } from './api';
import { Uc2Charts } from './Uc2Card';

export interface CompareUnitsProps {
  useCase: string;
  a: string;
  b: string;
  /** `#uc2Practice` — the group both practice charts are read against. */
  practice: string;
  onClose: () => void;
}

export function CompareUnits({ useCase, a, b, practice, onClose }: CompareUnitsProps) {
  const { t } = useTranslation();
  const compare = useIntelCompare(useCase, a, b);
  const data = compare.data;

  return (
    <div id="uc2Compare" className="result-box">
      <h5>{t('intel.uc2.compareTitle')}</h5>
      <div className="result-pad">
        {data ? (
          <div className="uc2-compare">
            {(['a', 'b'] as const).map((side) => (
              <div key={side} className="uc2-compare-side" data-testid={`uc2-compare-${side}`}>
                <h6>{data[side].unit.name}</h6>
                <p className="sub">{data[side].category_label}</p>
                <Uc2Charts detail={data[side]} practice={practice} testPrefix={side} />
              </div>
            ))}
          </div>
        ) : (
          <div className="ad-status">{t('analysis.progress')}</div>
        )}
        <div className="ad-actions">
          <button type="button" className="btn ghost sm" id="uc2CompareClose" onClick={onClose}>
            {t('widget.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompareUnits;
